/**
 * Ошибки API в человеческом виде (docs/06 §5.6): текст + действие + код
 * мелким шрифтом. Сообщения сервера уже на русском, поэтому используется
 * серверный `message`, а `code` остаётся для поддержки.
 */

export type ApiErrorDetailsItem = {
  readonly path: string;
  readonly message: string;
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: readonly ApiErrorDetailsItem[];
  readonly requestId: string | null;

  constructor(params: {
    code: string;
    message: string;
    status: number;
    details?: readonly ApiErrorDetailsItem[];
    requestId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
    this.requestId = params.requestId ?? null;
  }

  /** Ошибка сети/транспорта (fetch упал) — отдельный код, чтобы UI знал. */
  static network(message: string): ApiError {
    return new ApiError({ code: 'NETWORK_ERROR', message, status: 0 });
  }

  isUnauthenticated(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }
}

/** Тексты для кодов, у которых сервер не даёт понятной фразы. */
const CODE_HINTS: Readonly<Record<string, string>> = {
  NETWORK_ERROR: 'Нет связи с сервером. Проверьте соединение и повторите.',
  VALIDATION_ERROR: 'Проверьте заполненные поля.',
  UNAUTHENTICATED: 'Сессия истекла. Войдите заново.',
  FORBIDDEN: 'Недостаточно прав для этого действия.',
  NOT_FOUND: 'Запись не найдена — возможно, её удалили.',
  CONFLICT: 'Данные уже изменились. Обновите страницу и повторите.',
  WIP_LIMIT_EXCEEDED: 'Колонка переполнена: достигнут WIP-лимит.',
  ACTIVE_SPRINT_EXISTS: 'У проекта уже есть активный спринт. Сначала завершите его.',
  SPRINT_NOT_EMPTY: 'В спринте есть задачи — удалить его нельзя.',
  SPRINT_EMPTY: 'Нельзя запустить спринт без задач.',
  SPRINT_NOT_ACTIVE: 'Спринт не активен.',
  STATUS_IN_USE: 'Статус используется задачами — сначала перенесите их.',
  LAST_ADMIN: 'В системе должен остаться хотя бы один администратор.',
  LAST_PROJECT_ADMIN: 'В проекте должен остаться хотя бы один администратор проекта.',
  PROJECT_ARCHIVED: 'Проект в архиве: изменения недоступны.',
  TRANSITION_NOT_ALLOWED: 'Переход недоступен для этого статуса.',
  RESOLUTION_REQUIRED: 'Для этого перехода нужно выбрать резолюцию.',
  INVALID_HIERARCHY: 'Нарушена иерархия задач.',
  PAYLOAD_TOO_LARGE: 'Файл больше допустимого размера.',
  UNSUPPORTED_MEDIA_TYPE: 'Такой тип файла загрузить нельзя.',
  RATE_LIMITED: 'Слишком много попыток. Подождите и повторите.',
  JQL_SYNTAX_ERROR: 'В запросе синтаксическая ошибка.',
  INTERNAL_ERROR: 'Внутренняя ошибка сервера. Повторите позже.',
};

export type HumanError = {
  /** Что случилось — одна фраза. */
  readonly message: string;
  /** Код для поддержки; показывается мелким текстом. */
  readonly code: string;
  /** Можно ли предложить «Повторить». */
  readonly isRetryable: boolean;
};

export function humanizeError(error: unknown): HumanError {
  if (error instanceof ApiError) {
    const hint = CODE_HINTS[error.code];
    const message = error.message.length > 0 ? error.message : (hint ?? 'Не удалось выполнить действие.');
    return {
      message,
      code: error.code,
      isRetryable: error.code === 'NETWORK_ERROR' || error.status >= 500,
    };
  }
  if (error instanceof Error) {
    return { message: error.message, code: 'UNKNOWN', isRetryable: false };
  }
  return { message: 'Не удалось выполнить действие.', code: 'UNKNOWN', isRetryable: false };
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
